import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-collapse.js";
import "../pd-collapse-group.js";
import "../pd-timeline.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
//#region src/pd-timeline-wizard/PdTimelineWizard.ts
/**
* Wizard component combining timeline navigation with step content.
*
* @tagname pd-timeline-wizard
* @summary Timeline wizard with horizontal or vertical accordion layout.
*
* @slot content-[index] - Named slots for step content (content-0, content-1, etc.).
*
* @cssprop --pd-wizard-gap - Gap between timeline and content. Default: `16px`.
* @cssprop --pd-wizard-content-max-width - Maximum width of content area. Default: `80ch`.
* @cssprop --pd-wizard-content-padding - Padding around content area. Default: `0.5rem 0`.
*/
var PdTimelineWizard = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.orientation = "horizontal";
		this.steps = [];
		this.defaultActiveIndex = 0;
		this.clickable = false;
		this._internalActive = 0;
	}
	connectedCallback() {
		super.connectedCallback();
		if (this.activeIndex == null) this._internalActive = Math.min(Math.max(0, this.defaultActiveIndex || 0), Math.max(0, this.steps.length - 1));
	}
	/** @ignore */
	get _current() {
		return this.activeIndex ?? this._internalActive ?? 0;
	}
	static {
		this.styles = css`
    :host {
      display: block;
      --pd-wizard-gap: 16px;
      --pd-wizard-content-max-width: 80ch;
      --pd-wizard-content-padding: 0.5rem 0;
    }
    .h-wrap {
      display: grid;
      gap: var(--pd-wizard-gap);
    }
    .h-content {
      max-inline-size: var(--pd-wizard-content-max-width);
      padding: var(--pd-wizard-content-padding);
    }

    /* Vertikal: wir lassen Timeline die Labels rendern (als <pd-collapse>),
       daher braucht der Wizard nur Flow-Abstand rundum */
    .v-wrap {
      display: block;
    }
    pd-collapse {
      display: block;
      margin-block: 6px;
    }
  `;
	}
	/** Points für die Timeline aus Steps ableiten */
	_points() {
		return this.steps.map((s, i) => ({
			id: s.id ?? `step-${i}`,
			text: String(i + 1),
			state: s.state,
			labelSide: s.labelSide ?? (this.orientation === "vertical" ? "back" : "back")
		}));
	}
	_renderHorizontal() {
		const points = this._points();
		const a = this._current;
		return html`
      <div class="h-wrap">
        <pd-timeline
          .orientation=${"horizontal"}
          .points=${points}
          .activeIndex=${a}
          .clickable=${this.clickable}
        >
          ${this.steps.map((s, i) => html` <span slot="${`label-${i}`}">${s.label}</span> `)}
        </pd-timeline>

        <div class="h-content">
          ${this.steps.map((_, i) => i === a ? html`<slot name="${`content-${i}`}"></slot>` : nothing)}
        </div>
      </div>
    `;
	}
	_renderVertical() {
		const points = this._points();
		const a = this._current;
		return html`
      <div class="v-wrap">
        <pd-collapse-group>
          <pd-timeline
            .orientation=${"vertical"}
            .points=${points}
            .activeIndex=${a}
            .clickable=${this.clickable}
          >
            ${this.steps.map((s, i) => html`
                <pd-collapse id="${`collapseId${i}`}" slot="${`label-${i}`}">
                  <div slot="header">${s.label}</div>
                  <div class="section" slot="content">
                    <slot name="${`content-${i}`}"></slot>
                  </div>
                </pd-collapse>
              `)}
          </pd-timeline>
        </pd-collapse-group>
      </div>
    `;
	}
	render() {
		return this.orientation === "horizontal" ? this._renderHorizontal() : this._renderVertical();
	}
};
__decorate([property({ reflect: true })], PdTimelineWizard.prototype, "orientation", void 0);
__decorate([property({ type: Array })], PdTimelineWizard.prototype, "steps", void 0);
__decorate([property({ type: Number })], PdTimelineWizard.prototype, "activeIndex", void 0);
__decorate([property({ type: Number })], PdTimelineWizard.prototype, "defaultActiveIndex", void 0);
__decorate([property({ type: Boolean })], PdTimelineWizard.prototype, "clickable", void 0);
__decorate([state()], PdTimelineWizard.prototype, "_internalActive", void 0);
//#endregion
export { PdTimelineWizard };
